/**
 * Notification Service
 *
 * Reads and manages notifications stored by Cloud Functions at notifications/{uid}.
 */

import { ref, get, set, remove, query, orderByChild, onValue, off } from 'firebase/database'
import { db } from '@/lib/firebase'

export interface Notification {
  id: string
  type: 'review_reminder' | 'streak_risk' | 'achievement_earned' | 'general'
  title: string
  message: string
  lessonId?: string
  read: boolean
  createdAt: number
}

/**
 * Get all notifications for a user (one-time fetch)
 */
export async function getNotifications(uid: string): Promise<Notification[]> {
  const notifRef = query(ref(db, `notifications/${uid}`), orderByChild('createdAt'))
  const snapshot = await get(notifRef)
  if (!snapshot.exists()) return []

  const items: Notification[] = []
  snapshot.forEach(child => {
    items.push({ id: child.key!, ...child.val() })
  })

  // Newest first
  return items.reverse()
}

/**
 * Subscribe to notifications in real-time. Returns unsubscribe function.
 */
export function subscribeNotifications(
  uid: string,
  callback: (notifications: Notification[]) => void,
): () => void {
  const notifRef = query(ref(db, `notifications/${uid}`), orderByChild('createdAt'))

  const handler = onValue(notifRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback([])
      return
    }
    const items: Notification[] = []
    snapshot.forEach(child => {
      items.push({ id: child.key!, ...child.val() })
    })
    callback(items.reverse())
  })

  // onValue returns the unsubscribe function indirectly — we do it manually
  return () => off(notifRef, 'value', handler)
}

/**
 * Mark a single notification as read
 */
export async function markNotificationRead(uid: string, notificationId: string): Promise<void> {
  await set(ref(db, `notifications/${uid}/${notificationId}/read`), true)
}

/**
 * Mark all notifications as read
 */
export async function markAllRead(uid: string): Promise<void> {
  const snapshot = await get(ref(db, `notifications/${uid}`))
  if (!snapshot.exists()) return

  const updates: Record<string, boolean> = {}
  snapshot.forEach(child => {
    updates[`notifications/${uid}/${child.key}/read`] = true
  })

  // Batch update via individual sets (RTDB doesn't have multi-path update client-side easily)
  await Promise.all(
    Object.entries(updates).map(([path, val]) => set(ref(db, path), val))
  )
}

/**
 * Get count of unread notifications
 */
export async function getUnreadCount(uid: string): Promise<number> {
  const snapshot = await get(ref(db, `notifications/${uid}`))
  if (!snapshot.exists()) return 0

  let count = 0
  snapshot.forEach(child => {
    if (!child.val().read) count++
  })
  return count
}

/**
 * Remove notifications older than a given number of days
 */
export async function clearOldNotifications(uid: string, daysOld: number = 30): Promise<void> {
  const cutoff = Date.now() - daysOld * 24 * 60 * 60 * 1000
  const snapshot = await get(ref(db, `notifications/${uid}`))
  if (!snapshot.exists()) return

  const deletePromises: Promise<void>[] = []
  snapshot.forEach(child => {
    if (child.val().createdAt < cutoff) {
      deletePromises.push(remove(ref(db, `notifications/${uid}/${child.key}`)))
    }
  })

  await Promise.all(deletePromises)
}
